import React, { useState, useEffect, useMemo, useRef } from 'react';
import apiClient from '../../utils/apiClient';
import downloadFile from '../../utils/fileDownloader';
import { useAuth } from '@/App';
import { toast } from 'sonner';
import {
  FileDown,
  Calendar,
  Users,
  ClipboardCheck,
  Award,
  Mail,
  Activity,
  ShieldCheck,
  Search,
  BookOpen,
  Sparkles,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Filter,
  Download,
  ChevronRight,
  UserCheck,
  X,
  Layers,
  FileSpreadsheet,
  Building2,
  Phone,
  Heart,
  TrendingUp,
  RefreshCw,
  SlidersHorizontal,
  ChevronDown,
  GraduationCap
} from 'lucide-react';

const DataExport = () => {
  const { user } = useAuth();
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('excel');
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);

  // Dossier Scope: 'individual' | 'class' | 'all'
  const [dossierScope, setDossierScope] = useState('individual');

  // Lightweight filter options from optimized backend
  const [classes, setClasses] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [stats, setStats] = useState({ total_students: 0, total_parents: 0 });

  // Lightweight student search state (no mass-downloading of all students!)
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);

  // Dossier Live Preview State
  const [dossierPreview, setDossierPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Whole-School Attendance Audit State
  const [attendanceDateMode, setAttendanceDateMode] = useState('single'); // 'single' | 'range'
  const [attendanceSnapshotDate, setAttendanceSnapshotDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [attendanceAuditPreview, setAttendanceAuditPreview] = useState(null);
  const [attendanceAuditLoading, setAttendanceAuditLoading] = useState(false);

  // Active filters
  const [filters, setFilters] = useState({
    reportType: 'student_dossier',
    className: '',
    wardenId: '',
    studentId: '',
    parentId: '',
    gender: '',
    bloodGroup: '',
    timeWindow: 'last_30_days',
    startDate: '',
    endDate: ''
  });

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 1. Fetch Lightweight Filter Options (Only classes & wardens, NOT all student records!)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setFilterOptionsLoading(true);
        const res = await apiClient.get('/v1/admin/reports/filter-options');
        setClasses(res.data.classes || []);
        setWardens(res.data.wardens || []);
        setStats({
          total_students: res.data.total_students || 0,
          total_parents: res.data.total_parents || 0
        });
      } catch (err) {
        console.error('Failed to load filter options:', err);
        toast.error('Failed to load filter parameters');
      } finally {
        setFilterOptionsLoading(false);
      }
    };
    if (user?.hostel_id) fetchFilterOptions();
  }, [user]);

  // 2. High-Performance Debounced Student Search (Only fetches 15-20 lightweight items on demand)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (filters.reportType !== 'student_dossier' || dossierScope !== 'individual') return;

      try {
        setSearchingStudents(true);
        const params = { limit: 20 };
        if (studentSearchInput.trim()) params.q = studentSearchInput.trim();
        if (filters.className) params.class_name = filters.className;

        const res = await apiClient.get('/v1/admin/reports/students-search', { params });
        const list = res.data || [];
        setStudentSearchResults(list);

        // If no student is currently selected for individual dossier, auto-pick the first match
        if (!selectedStudent && list.length > 0 && filters.reportType === 'student_dossier' && dossierScope === 'individual') {
          setSelectedStudent(list[0]);
          setFilters(prev => ({ ...prev, studentId: list[0].id }));
        }
      } catch (err) {
        console.error('Student search error:', err);
      } finally {
        setSearchingStudents(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [studentSearchInput, filters.className, filters.reportType, dossierScope, isSearchOpen]);

  // 3. Live Preview for Student 360° Dossier (Individual scope only)
  useEffect(() => {
    if (filters.reportType !== 'student_dossier' || dossierScope !== 'individual' || !filters.studentId) {
      setDossierPreview(null);
      return;
    }

    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        const params = {
          student_id: filters.studentId,
          time_window: filters.timeWindow
        };
        if (filters.timeWindow === 'custom') {
          if (filters.startDate) params.start_date = filters.startDate;
          if (filters.endDate) params.end_date = filters.endDate;
        }

        const res = await apiClient.get('/v1/admin/reports/student-dossier/preview', { params });
        setDossierPreview(res.data);
      } catch (err) {
        console.error('Dossier preview error:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [filters.reportType, dossierScope, filters.studentId, filters.timeWindow, filters.startDate, filters.endDate]);

  // 4. Live Preview for Whole-School Attendance Audit Report (Night-Curfew Matrix)
  useEffect(() => {
    if (filters.reportType !== 'attendance') {
      setAttendanceAuditPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setAttendanceAuditLoading(true);
        const params = {
          date_mode: attendanceDateMode,
          status_filter: attendanceStatusFilter
        };

        if (attendanceDateMode === 'single') {
          params.date = attendanceSnapshotDate;
        } else {
          if (filters.startDate) params.start_date = filters.startDate;
          if (filters.endDate) params.end_date = filters.endDate;
        }

        if (filters.className) params.class_name = filters.className;
        if (filters.gender) params.gender = filters.gender;
        if (filters.wardenId) params.warden_id = filters.wardenId;

        const res = await apiClient.get('/v1/admin/reports/attendance-audit/preview', { params });
        setAttendanceAuditPreview(res.data);
      } catch (err) {
        console.error('Attendance audit preview error:', err);
      } finally {
        setAttendanceAuditLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [
    filters.reportType,
    attendanceDateMode,
    attendanceSnapshotDate,
    attendanceStatusFilter,
    filters.startDate,
    filters.endDate,
    filters.className,
    filters.gender,
    filters.wardenId
  ]);

  // Handle Export / Download
  const handleExport = async (formatOverride) => {
    const format = formatOverride || downloadFormat;

    if (filters.reportType === 'student_dossier') {
      if (dossierScope === 'individual' && !filters.studentId) {
        toast.error('Please select a student to generate the Student Dossier.');
        return;
      }
      if (dossierScope === 'class' && !filters.className) {
        toast.error('Please select a class to generate the Class Dossier.');
        return;
      }
    }

    setExportLoading(true);
    try {
      const params = {
        report_type: filters.reportType,
        format: format
      };

      if (filters.className) params.class_name = filters.className;
      if (filters.wardenId) params.warden_id = filters.wardenId;
      if (filters.gender) params.gender = filters.gender;
      if (filters.bloodGroup) params.blood_group = filters.bloodGroup;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.timeWindow) params.time_window = filters.timeWindow;

      // In individual mode, pass studentId; in batch/class/all mode, leave student_id empty so backend generates batch!
      if (filters.reportType === 'student_dossier') {
        if (dossierScope === 'individual' && filters.studentId) {
          params.student_id = filters.studentId;
        }
      } else if (filters.reportType === 'attendance') {
        params.date_mode = attendanceDateMode;
        if (attendanceDateMode === 'single') {
          params.date = attendanceSnapshotDate;
        }
        if (attendanceStatusFilter && attendanceStatusFilter !== 'all') {
          params.status_filter = attendanceStatusFilter;
        }
      } else if (filters.studentId) {
        params.student_id = filters.studentId;
      }

      const response = await apiClient.get('/v1/admin/reports/generate', {
        params,
        responseType: 'blob'
      });

      let mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      let extension = 'xlsx';

      if (format === 'csv') {
        mimeType = 'text/csv';
        extension = 'csv';
      } else if (format === 'pdf') {
        mimeType = 'application/pdf';
        extension = 'pdf';
      } else if (format === 'json') {
        mimeType = 'application/json';
        extension = 'json';
      }

      let filename = `${filters.reportType}_report_${new Date().toISOString().split('T')[0]}.${extension}`;
      if (filters.reportType === 'student_dossier') {
        if (dossierScope === 'individual' && selectedStudent) {
          const cleanName = selectedStudent.name.replace(/[^a-zA-Z0-9]/g, '_');
          filename = `Student360_Dossier_${cleanName}_${selectedStudent.readable_id || 'ID'}.${extension}`;
        } else if (dossierScope === 'class' && filters.className) {
          const cleanClass = filters.className.replace(/[^a-zA-Z0-9]/g, '_');
          filename = `Dossier_Binder_${cleanClass}_${new Date().toISOString().split('T')[0]}.${extension}`;
        } else {
          filename = `Dossier_Binder_All_Students_${new Date().toISOString().split('T')[0]}.${extension}`;
        }
      } else if (filters.reportType === 'attendance') {
        const classSlug = filters.className ? filters.className.replace(/[^a-zA-Z0-9]/g, '_') : 'AllClasses';
        const dateSlug = attendanceDateMode === 'single' ? (attendanceSnapshotDate || 'Daily') : `${filters.startDate || 'start'}_to_${filters.endDate || 'end'}`;
        filename = `WholeSchool_Attendance_Audit_${classSlug}_${dateSlug}.${extension}`;
      }

      await downloadFile(response.data, filename, mimeType);

      toast.success(
        `Report successfully downloaded (${extension.toUpperCase()})`
      );
    } catch (error) {
      console.error('Export download error:', error);
      if (!error.response) {
        toast.error('Failed to export report. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Report suites definition
  const reportSuites = [
    {
      group: 'Featured Dossier',
      reports: [
        {
          id: 'student_dossier',
          title: 'Student 360° Comprehensive Dossier',
          subtitle: 'Holistic Student Welfare Card',
          description: 'Consolidated student welfare footprint: attendance %, health alerts, latest examination %, and dispatched parent notices.',
          icon: ShieldCheck,
          badge: 'Executive Card',
          color: 'blue'
        }
      ]
    },
    {
      group: 'Institutional Registers',
      reports: [
        {
          id: 'students',
          title: 'Student Directory Register',
          subtitle: 'Institutional Enrollment Roster',
          description: 'Master list of enrolled students, room allotments, assigned resident wardens, and demographic profiles.',
          icon: Users,
          color: 'slate'
        },
        {
          id: 'attendance',
          title: 'Whole-School Attendance Audit',
          subtitle: 'Class-wise & M/F Matrix (Night-Curfew)',
          description: 'Enables Directors, Principals, and Chief Wardens to review night-curfew roll calls with class-wise and gender headcount matrices and detailed boarder roll registers.',
          icon: ClipboardCheck,
          badge: 'Curfew Audit',
          color: 'emerald'
        },
        {
          id: 'results',
          title: 'Academic Results & Standing',
          subtitle: 'Examination Marks & Percentages',
          description: 'Unified gradebook records across all semesters, examinations, subjects, and grade distributions.',
          icon: Award,
          color: 'amber'
        }
      ]
    },
    {
      group: 'Pastoral & Guardian Services',
      reports: [
        {
          id: 'health',
          title: 'Health & Medical Records',
          subtitle: 'Infirmary Logs & Medical History',
          description: 'Student medical records, active illnesses, allergy/disease alerts, and infirmary visits.',
          icon: Activity,
          color: 'rose'
        },
        {
          id: 'parents',
          title: 'Parent & Guardian Directory',
          subtitle: 'Family Contacts & Addresses',
          description: 'Contact register of verified parents and local guardians with phone numbers, emails, and residential addresses.',
          icon: Mail,
          color: 'indigo'
        }
      ]
    }
  ];

  const currentReport = useMemo(() => {
    for (const suite of reportSuites) {
      const match = suite.reports.find(r => r.id === filters.reportType);
      if (match) return match;
    }
    return reportSuites[0].reports[0];
  }, [filters.reportType]);

  const selectStudent = (student) => {
    setSelectedStudent(student);
    setFilters(prev => ({ ...prev, studentId: student.id }));
    setStudentSearchInput('');
    setIsSearchOpen(false);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in text-slate-800">
      {/* Executive Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#1A4D6F] to-[#2C5F7F] text-white flex items-center justify-center shadow-md shadow-blue-900/10">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl md:text-3xl font-extrabold text-[#1A4D6F] tracking-tight">
                Institutional Reporting Center
              </h1>
              <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200/80">
                Admin Console
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Generate executive dossiers, academic report cards, and comprehensive data registers.
            </p>
          </div>
        </div>

        {/* Quick Institutional Stats Pill */}
        <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-slate-200 shadow-xs self-start md:self-auto">
          <div className="flex items-center gap-2 pr-3 border-r border-slate-200 text-xs">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="font-semibold text-slate-700">{stats.total_students}</span>
            <span className="text-slate-400">Students</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Building2 className="w-4 h-4 text-slate-400" />
            <span className="font-semibold text-slate-700">{classes.length}</span>
            <span className="text-slate-400">Classes</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Left Navigation & Right Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Report Suites Selector (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Select Report Suite
            </h2>
            <span className="text-[11px] font-medium text-slate-400">
              6 Modules Available
            </span>
          </div>

          <div className="space-y-5">
            {reportSuites.map((suite, sIdx) => (
              <div key={sIdx} className="space-y-2">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-2">
                  {suite.group}
                </p>
                <div className="space-y-1.5">
                  {suite.reports.map((rep) => {
                    const isSelected = filters.reportType === rep.id;
                    const Icon = rep.icon;
                    return (
                      <button
                        key={rep.id}
                        onClick={() => setFilters(prev => ({ ...prev, reportType: rep.id }))}
                        className={`w-full text-left p-3.5 rounded-xl border transition-all duration-150 flex items-start gap-3.5 group relative ${isSelected
                            ? 'bg-gradient-to-r from-blue-50/80 via-white to-white border-[#2C5F7F] shadow-sm ring-1 ring-[#2C5F7F]/20'
                            : 'bg-white border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/80'
                          }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isSelected
                              ? 'bg-[#1A4D6F] text-white shadow-xs'
                              : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-700'
                            }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0 pr-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-bold truncate ${isSelected ? 'text-[#1A4D6F]' : 'text-slate-700 group-hover:text-slate-900'
                                }`}
                            >
                              {rep.title}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
                            {rep.subtitle}
                          </p>
                        </div>
                        {rep.badge && (
                          <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100/80 text-[#1A4D6F] self-center">
                            {rep.badge}
                          </span>
                        )}
                        {isSelected && (
                          <div className="w-1.5 h-6 bg-[#2C5F7F] rounded-full absolute -left-0.5 top-1/2 -translate-y-1/2"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Active Workspace (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Active Suite Banner */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1A4D6F] flex items-center justify-center border border-blue-100">
                  <currentReport.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    {currentReport.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {currentReport.description}
                  </p>
                </div>
              </div>
              <span className="text-xs font-semibold px-3 py-1 bg-slate-100 text-slate-600 rounded-full self-start sm:self-auto border border-slate-200">
                {filters.reportType === 'student_dossier'
                  ? (dossierScope === 'individual' ? 'Single Student Card' : dossierScope === 'class' ? 'Class Welfare Binder' : 'All Students Binder')
                  : filters.reportType === 'attendance'
                    ? 'Curfew Roll Call Audit'
                    : 'Collective Register'}
              </span>
            </div>

            {/* Config & Filter Controls */}
            <div className="mt-6 pt-6 border-t border-slate-100 space-y-6">
              {/* ========================================================= */}
              {/* DOSSIER SPECIFIC CONTROLS                                 */}
              {/* ========================================================= */}
              {filters.reportType === 'student_dossier' ? (
                <div className="space-y-6">
                  {/* Scope Selector: Individual vs Entire Class vs All Students */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <GraduationCap className="w-3.5 h-3.5 text-blue-600" /> Dossier Generation Scope
                      </label>
                      <span className="text-[11px] text-slate-400 font-medium">
                        {dossierScope === 'individual' && '1 Student Selected'}
                        {dossierScope === 'class' && (filters.className ? `Cohort: ${filters.className}` : 'Pick Class')}
                        {dossierScope === 'all' && `Full Institution (${stats.total_students} Students)`}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 p-1.5 bg-slate-100 rounded-xl border border-slate-200/80">
                      <button
                        type="button"
                        onClick={() => {
                          setDossierScope('individual');
                          if (selectedStudent) {
                            setFilters(prev => ({ ...prev, studentId: selectedStudent.id }));
                          }
                        }}
                        className={`py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${dossierScope === 'individual'
                            ? 'bg-white text-[#1A4D6F] shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        Individual Student
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDossierScope('class');
                          setFilters(prev => ({
                            ...prev,
                            studentId: '',
                            className: prev.className || (classes.length > 0 ? classes[0] : '')
                          }));
                        }}
                        className={`py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${dossierScope === 'class'
                            ? 'bg-white text-[#1A4D6F] shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        Entire Class / Section
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDossierScope('all');
                          setFilters(prev => ({ ...prev, studentId: '', className: '' }));
                        }}
                        className={`py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${dossierScope === 'all'
                            ? 'bg-white text-[#1A4D6F] shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        All Enrolled Students
                      </button>
                    </div>
                  </div>

                  {/* SCOPE 1: INDIVIDUAL STUDENT SEARCH */}
                  {dossierScope === 'individual' && (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                        <span>Select Student</span>
                        <span className="text-[11px] text-slate-400 font-normal">
                          Fast Search & Live Preview
                        </span>
                      </label>

                      {/* Filter bar: Class Select + Smart Combobox */}
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3" ref={searchContainerRef}>
                        <div className="sm:col-span-4">
                          <select
                            className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                            value={filters.className}
                            onChange={(e) => {
                              setFilters(prev => ({ ...prev, className: e.target.value }));
                            }}
                          >
                            <option value="">All Classes</option>
                            {classes.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>

                        <div className="sm:col-span-8 relative">
                          <div
                            onClick={() => setIsSearchOpen(true)}
                            className="w-full h-11 px-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between cursor-pointer focus-within:bg-white focus-within:ring-2 focus-within:ring-[#2C5F7F]/30 transition-all"
                          >
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <input
                                type="text"
                                placeholder={selectedStudent ? `${selectedStudent.name} (${selectedStudent.readable_id || 'STU'})` : 'Type name, roll # or room...'}
                                className="w-full bg-transparent border-none outline-none text-xs font-medium text-slate-700 placeholder:text-slate-400"
                                value={studentSearchInput}
                                onChange={(e) => {
                                  setStudentSearchInput(e.target.value);
                                  setIsSearchOpen(true);
                                }}
                                onFocus={() => setIsSearchOpen(true)}
                              />
                            </div>
                            {searchingStudents ? (
                              <RefreshCw className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>

                          {/* Floating Search Dropdown */}
                          {isSearchOpen && (
                            <div className="absolute left-0 right-0 top-12 z-30 bg-white rounded-xl border border-slate-200 shadow-xl max-h-60 overflow-y-auto p-1.5 space-y-1">
                              {studentSearchResults.length > 0 ? (
                                studentSearchResults.map(s => {
                                  const isSelected = selectedStudent?.id === s.id;
                                  return (
                                    <div
                                      key={s.id}
                                      onClick={() => selectStudent(s)}
                                      className={`p-2.5 rounded-lg flex items-center justify-between cursor-pointer transition-colors ${isSelected
                                          ? 'bg-blue-50 text-[#1A4D6F] font-semibold'
                                          : 'hover:bg-slate-50 text-slate-700'
                                        }`}
                                    >
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                          {s.name ? s.name.charAt(0).toUpperCase() : 'S'}
                                        </div>
                                        <div className="truncate">
                                          <p className="text-xs font-bold leading-tight truncate">{s.name}</p>
                                          <p className="text-[10px] text-slate-400">
                                            Roll: {s.readable_id || 'N/A'} &bull; {s.class_name || 'No class'} {s.room_number ? `(Rm ${s.room_number})` : ''}
                                          </p>
                                        </div>
                                      </div>
                                      {isSelected && <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="py-4 text-center text-xs text-slate-400">
                                  No students match query
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Active Selected Student Pill */}
                      {selectedStudent && (
                        <div className="flex items-center justify-between p-3 bg-blue-50/60 rounded-xl border border-blue-100 text-xs">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-[#1A4D6F] text-white flex items-center justify-center font-bold text-xs">
                              {selectedStudent.name ? selectedStudent.name.charAt(0).toUpperCase() : 'S'}
                            </div>
                            <div>
                              <span className="font-bold text-slate-800">{selectedStudent.name}</span>
                              <span className="text-slate-400 mx-1.5">&bull;</span>
                              <span className="text-slate-600 font-medium">Roll: {selectedStudent.readable_id || 'ID'}</span>
                              <span className="text-slate-400 mx-1.5">&bull;</span>
                              <span className="text-blue-700 font-medium">{selectedStudent.class_name} {selectedStudent.room_number ? `(Rm ${selectedStudent.room_number})` : ''}</span>
                            </div>
                          </div>
                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                            Active Target
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SCOPE 2: ENTIRE CLASS SELECTION */}
                  {dossierScope === 'class' && (
                    <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <label className="block text-xs font-bold text-[#1A4D6F] uppercase tracking-wider mb-1">
                            Choose Target Class
                          </label>
                          <p className="text-xs text-slate-500">
                            Compiles individual welfare cards for all students enrolled in this class into a single binder.
                          </p>
                        </div>
                        <select
                          className="h-11 px-4 bg-white border border-slate-200 rounded-xl text-xs font-bold text-[#1A4D6F] focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all self-start sm:self-auto min-w-[180px]"
                          value={filters.className}
                          onChange={(e) => setFilters(prev => ({ ...prev, className: e.target.value }))}
                        >
                          <option value="">-- Select Class --</option>
                          {classes.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>

                      <div className="p-3 bg-white rounded-xl border border-blue-100/80 text-xs text-slate-600 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span>
                          <strong>Batch Mode:</strong> PDF generates a multi-page binder with page breaks between each student. Excel generates an executive Class Welfare Matrix with all students & notice ledgers.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* SCOPE 3: ALL STUDENTS IN INSTITUTION */}
                  {dossierScope === 'all' && (
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#1A4D6F]" />
                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                          Full Institutional Welfare Binder ({stats.total_students} Students Across {classes.length} Classes)
                        </h4>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Compiles complete 360° dossiers for all enrolled students in the institution. Recommended format: <strong>Excel (.xlsx)</strong> for comprehensive spreadsheet analysis or <strong>PDF Binder</strong> for master institutional printing.
                      </p>
                    </div>
                  )}

                  {/* Common: Welfare Term / Time Window */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-blue-600" /> Welfare Term / Time Window
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { id: 'last_30_days', label: 'Last 30 Days' },
                        { id: 'current_term', label: 'Current Term (90d)' },
                        { id: 'full_year', label: 'Academic Year' },
                        { id: 'custom', label: 'Custom Range...' }
                      ].map(tw => {
                        const isSelected = filters.timeWindow === tw.id;
                        return (
                          <button
                            key={tw.id}
                            onClick={() => setFilters(prev => ({ ...prev, timeWindow: tw.id }))}
                            className={`py-2 px-3 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${isSelected
                                ? 'bg-[#1A4D6F] text-white border-[#1A4D6F] shadow-xs'
                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                              }`}
                          >
                            {tw.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Custom Date Pickers */}
                    {filters.timeWindow === 'custom' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Start Date</label>
                          <input
                            type="date"
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                            value={filters.startDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-500 mb-1">End Date</label>
                          <input
                            type="date"
                            className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                            value={filters.endDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Individual Live Preview Card (Only in individual scope) */}
                  {dossierScope === 'individual' && dossierPreview && (
                    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5 space-y-4">
                      <div className="flex items-center justify-between pb-3 border-b border-slate-200/80">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Holistic Student Welfare Preview
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400">
                          Window: {dossierPreview.time_window.label}
                        </span>
                      </div>

                      {/* 4 Core Welfare KPIs in Modern Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-xs text-center">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Attendance</p>
                          <p className="text-xl font-extrabold text-[#1A4D6F] mt-1">
                            {dossierPreview.attendance.percentage}%
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{dossierPreview.attendance.ratio_str}</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-xs text-center">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Health Status</p>
                          <p className="text-xl font-extrabold text-emerald-600 mt-1">
                            {dossierPreview.health.status}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Blood: {dossierPreview.health.blood_group}</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-xs text-center">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Academic Avg</p>
                          <p className="text-xl font-extrabold text-indigo-600 mt-1">
                            {dossierPreview.academics.average_percentage}%
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{dossierPreview.academics.exams_count} Exams</p>
                        </div>
                        <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-xs text-center">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Notices Dispatched</p>
                          <p className="text-xl font-extrabold text-amber-600 mt-1">
                            {dossierPreview.notices.total_count}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            {dossierPreview.notices.financial_count} Fin &bull; {dossierPreview.notices.general_count} Gen
                          </p>
                        </div>
                      </div>

                      {/* Examination Tags */}
                      {dossierPreview.academics.latest_exams.length > 0 && (
                        <div className="bg-white p-3 rounded-xl border border-slate-200/80">
                          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                            Latest Examination Scores
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {dossierPreview.academics.latest_exams.map((ex, idx) => (
                              <span
                                key={idx}
                                className="px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700 flex items-center gap-1.5"
                              >
                                <span>{ex.exam_name}:</span>
                                <span className="font-bold text-[#1A4D6F]">{ex.percentage}%</span>
                                <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200">
                                  {ex.grade}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : filters.reportType === 'attendance' ? (
                /* ========================================================= */
                /* WHOLE-SCHOOL ATTENDANCE AUDIT CONTROLS & LIVE MATRIX PREVIEW */
                /* ========================================================= */
                <div className="space-y-6">
                  {/* Top Bar: Curfew Roll Call Date Mode & Snapshot / Range Selector */}
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-[#1A4D6F]" /> Curfew Roll Call Time Window
                        </label>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Choose Single Date for a night roll call snapshot, or Date Range for longitudinal audit.
                        </p>
                      </div>

                      {/* Segmented Mode Selector */}
                      <div className="grid grid-cols-2 gap-1.5 p-1 bg-white rounded-xl border border-slate-200 self-start sm:self-auto">
                        <button
                          type="button"
                          onClick={() => setAttendanceDateMode('single')}
                          className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${attendanceDateMode === 'single'
                              ? 'bg-[#1A4D6F] text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                          Single Date (Snapshot)
                        </button>
                        <button
                          type="button"
                          onClick={() => setAttendanceDateMode('range')}
                          className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${attendanceDateMode === 'range'
                              ? 'bg-[#1A4D6F] text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                          Date Range (From — To)
                        </button>
                      </div>
                    </div>

                    {/* Date Inputs depending on Mode */}
                    {attendanceDateMode === 'single' ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2 border-t border-slate-200/60">
                        <div className="flex-1 max-w-xs">
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                            Snapshot Date
                          </label>
                          <input
                            type="date"
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                            value={attendanceSnapshotDate}
                            onChange={(e) => setAttendanceSnapshotDate(e.target.value)}
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-4 sm:pt-0">
                          <button
                            type="button"
                            onClick={() => setAttendanceSnapshotDate(new Date().toISOString().split('T')[0])}
                            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 cursor-pointer"
                          >
                            Today
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const d = new Date();
                              d.setDate(d.getDate() - 1);
                              setAttendanceSnapshotDate(d.toISOString().split('T')[0]);
                            }}
                            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900 cursor-pointer"
                          >
                            Yesterday
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                            From Date
                          </label>
                          <input
                            type="date"
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                            value={filters.startDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                            To Date
                          </label>
                          <input
                            type="date"
                            className="w-full h-10 px-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                            value={filters.endDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Filter Configuration Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Class Filter */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Class / Section
                      </label>
                      <select
                        className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                        value={filters.className}
                        onChange={(e) => setFilters(prev => ({ ...prev, className: e.target.value }))}
                      >
                        <option value="">All Classes (Whole School)</option>
                        {classes.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Gender Filter */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Gender Breakdown Scope
                      </label>
                      <select
                        className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                        value={filters.gender}
                        onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
                      >
                        <option value="">All Genders (Boys & Girls)</option>
                        <option value="boys">Boys (M) Only</option>
                        <option value="girls">Girls (F) Only</option>
                      </select>
                    </div>

                    {/* Curfew Roll Status Filter */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Curfew Status Filter
                      </label>
                      <select
                        className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                        value={attendanceStatusFilter}
                        onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                      >
                        <option value="all">All Curfew Statuses</option>
                        <option value="present">Present (Verified Roll Call)</option>
                        <option value="absent">Absent / Unaccounted (Alert)</option>
                        <option value="infirmary">In-Infirmary (Medical Care)</option>
                        <option value="leave">On Approved Leave / Gate Pass</option>
                      </select>
                    </div>
                  </div>

                  {/* ========================================================= */}
                  {/* LIVE EXECUTIVE NIGHT-CURFEW AUDIT PREVIEW                */}
                  {/* ========================================================= */}
                  {attendanceAuditLoading ? (
                    <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200/80 flex flex-col items-center justify-center text-center">
                      <RefreshCw className="w-6 h-6 text-[#1A4D6F] animate-spin mb-2" />
                      <p className="text-xs font-bold text-slate-700">Compiling Night-Curfew Roll Call Matrix...</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Aggregating attendance, infirmary records, and gate passes</p>
                    </div>
                  ) : attendanceAuditPreview ? (
                    <div className="space-y-4">
                      {/* KPI Summary Cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                        {/* Total Enrolled */}
                        <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-xs">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Enrolled Boarders</p>
                          <p className="text-lg font-bold text-slate-800 mt-1">
                            {attendanceAuditPreview.executive_summary.total_enrolled}
                          </p>
                          <p className="text-[10px] text-slate-500 font-medium">
                            M: {attendanceAuditPreview.executive_summary.boys_enrolled} | F: {attendanceAuditPreview.executive_summary.girls_enrolled}
                          </p>
                        </div>

                        {/* Present */}
                        <div className="p-3 bg-emerald-50/70 rounded-xl border border-emerald-200/80 shadow-xs">
                          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Present at Curfew</p>
                          <p className="text-lg font-bold text-emerald-800 mt-1">
                            {attendanceAuditPreview.executive_summary.present_pct}%
                          </p>
                          <p className="text-[10px] text-emerald-700 font-medium">
                            {attendanceAuditPreview.executive_summary.present_count} Boarders
                          </p>
                        </div>

                        {/* Absent */}
                        <div className={`p-3 rounded-xl border shadow-xs ${attendanceAuditPreview.executive_summary.absent_count > 0
                            ? 'bg-red-50/70 border-red-200/80'
                            : 'bg-slate-50 border-slate-200'
                          }`}>
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${attendanceAuditPreview.executive_summary.absent_count > 0 ? 'text-red-700' : 'text-slate-400'
                            }`}>
                            Absent / Unaccounted
                          </p>
                          <p className={`text-lg font-bold mt-1 ${attendanceAuditPreview.executive_summary.absent_count > 0 ? 'text-red-800' : 'text-slate-700'
                            }`}>
                            {attendanceAuditPreview.executive_summary.absent_count}
                          </p>
                          <p className={`text-[10px] font-medium ${attendanceAuditPreview.executive_summary.absent_count > 0 ? 'text-red-600' : 'text-slate-500'
                            }`}>
                            {attendanceAuditPreview.executive_summary.absent_pct}% of total
                          </p>
                        </div>

                        {/* In Infirmary */}
                        <div className="p-3 bg-amber-50/70 rounded-xl border border-amber-200/80 shadow-xs">
                          <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">In-Infirmary</p>
                          <p className="text-lg font-bold text-amber-900 mt-1">
                            {attendanceAuditPreview.executive_summary.infirmary_count}
                          </p>
                          <p className="text-[10px] text-amber-700 font-medium">Under Medical Care</p>
                        </div>

                        {/* On Approved Leave */}
                        <div className="p-3 bg-blue-50/70 rounded-xl border border-blue-200/80 shadow-xs">
                          <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">Approved Leave</p>
                          <p className="text-lg font-bold text-blue-900 mt-1">
                            {attendanceAuditPreview.executive_summary.leave_count}
                          </p>
                          <p className="text-[10px] text-blue-700 font-medium">Gate Pass Authorized</p>
                        </div>
                      </div>

                      {/* Class-wise & Gender Headcount Matrix Table */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Class-wise & Gender Headcount Matrix
                          </span>
                          <span className="text-[11px] text-slate-500 font-medium">
                            {attendanceAuditPreview.class_matrix.length} Classes Audited
                          </span>
                        </div>
                        <div className="overflow-x-auto max-h-64">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="bg-slate-100 text-[10px] font-bold text-slate-600 uppercase tracking-wider border-b border-slate-200">
                                <th className="py-2 px-3">Class / Cohort</th>
                                <th className="py-2 px-2 text-center">Enrolled (M/F/Tot)</th>
                                <th className="py-2 px-2 text-center text-emerald-700">Present (M/F/Tot)</th>
                                <th className="py-2 px-2 text-center">Present %</th>
                                <th className="py-2 px-2 text-center text-red-700">Absent</th>
                                <th className="py-2 px-2 text-center text-amber-700">Infirmary</th>
                                <th className="py-2 px-2 text-center text-blue-700">Leave</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {attendanceAuditPreview.class_matrix.map((cRow, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                                  <td className="py-2 px-3 font-bold text-slate-800">
                                    Class {cRow.class_name}
                                  </td>
                                  <td className="py-2 px-2 text-center text-slate-600">
                                    {cRow.enrolled.m} / {cRow.enrolled.f} / <span className="font-bold">{cRow.enrolled.total}</span>
                                  </td>
                                  <td className="py-2 px-2 text-center text-emerald-800 font-medium">
                                    {cRow.present.m} / {cRow.present.f} / <span className="font-bold">{cRow.present.total}</span>
                                  </td>
                                  <td className="py-2 px-2 text-center font-bold text-slate-700">
                                    {cRow.present.pct}%
                                  </td>
                                  <td className="py-2 px-2 text-center font-semibold text-red-700">
                                    {cRow.absent.total}
                                  </td>
                                  <td className="py-2 px-2 text-center text-amber-700 font-semibold">
                                    {cRow.infirmary.total}
                                  </td>
                                  <td className="py-2 px-2 text-center text-blue-700 font-semibold">
                                    {cRow.leave.total}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-slate-100/90 font-bold text-slate-800 border-t-2 border-slate-300">
                                <td className="py-2.5 px-3 uppercase text-[11px]">Total Headcount</td>
                                <td className="py-2.5 px-2 text-center">
                                  {attendanceAuditPreview.totals.enrolled.m} / {attendanceAuditPreview.totals.enrolled.f} / {attendanceAuditPreview.totals.enrolled.total}
                                </td>
                                <td className="py-2.5 px-2 text-center text-emerald-800">
                                  {attendanceAuditPreview.totals.present.m} / {attendanceAuditPreview.totals.present.f} / {attendanceAuditPreview.totals.present.total}
                                </td>
                                <td className="py-2.5 px-2 text-center text-[#1A4D6F]">
                                  {attendanceAuditPreview.totals.present.pct}%
                                </td>
                                <td className="py-2.5 px-2 text-center text-red-800">
                                  {attendanceAuditPreview.totals.absent.total}
                                </td>
                                <td className="py-2.5 px-2 text-center text-amber-800">
                                  {attendanceAuditPreview.totals.infirmary.total}
                                </td>
                                <td className="py-2.5 px-2 text-center text-blue-800">
                                  {attendanceAuditPreview.totals.leave.total}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                      {/* Boarder Roll Call Register Preview Snippet */}
                      {attendanceAuditPreview.boarder_register && attendanceAuditPreview.boarder_register.length > 0 && (
                        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/70">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                              Boarder Roll Call Register ({attendanceAuditPreview.boarder_register.length} matching records)
                            </span>
                            <span className="text-[10px] text-slate-400">
                              Previewing first 6 entries
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {attendanceAuditPreview.boarder_register.slice(0, 6).map((bRow, bIdx) => {
                              const isPres = bRow.status === 'Present';
                              const isAbs = bRow.status === 'Absent/Unaccounted';
                              const isInf = bRow.status === 'In-Infirmary';
                              return (
                                <div key={bIdx} className="p-2 bg-white rounded-lg border border-slate-200 text-xs flex items-center justify-between">
                                  <div>
                                    <p className="font-bold text-slate-800">{bRow.student_name}</p>
                                    <p className="text-[10px] text-slate-500">
                                      Class {bRow.class_name} • Room {bRow.room_number} • {bRow.gender}
                                    </p>
                                  </div>
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isPres ? 'bg-emerald-100 text-emerald-800' :
                                      isAbs ? 'bg-red-100 text-red-800' :
                                        isInf ? 'bg-amber-100 text-amber-800' :
                                          'bg-blue-100 text-blue-800'
                                    }`}>
                                    {bRow.status}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : (
                /* ========================================================= */
                /* GENERAL REGISTER SUITE FILTERS                            */
                /* ========================================================= */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Class Filter */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      Class / Section
                    </label>
                    <select
                      className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                      value={filters.className}
                      onChange={(e) => setFilters(prev => ({ ...prev, className: e.target.value }))}
                    >
                      <option value="">All Classes & Sections</option>
                      {classes.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Warden Filter */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      Assigned Warden
                    </label>
                    <select
                      className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none transition-all"
                      value={filters.wardenId}
                      onChange={(e) => setFilters(prev => ({ ...prev, wardenId: e.target.value }))}
                    >
                      <option value="">All Resident Wardens</option>
                      {wardens.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date Filters (Only for Results) */}
                  {filters.reportType === 'results' && (
                    <>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          Start Date
                        </label>
                        <input
                          type="date"
                          className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                          value={filters.startDate}
                          onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          End Date
                        </label>
                        <input
                          type="date"
                          className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                          value={filters.endDate}
                          onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </div>
                    </>
                  )}

                  {/* Demographic Filters (Gender & Blood Group) */}
                  {(filters.reportType === 'students' || filters.reportType === 'health') && (
                    <>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          Gender Filter
                        </label>
                        <select
                          className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                          value={filters.gender}
                          onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
                        >
                          <option value="">All Genders</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          Blood Group
                        </label>
                        <select
                          className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:ring-2 focus:ring-[#2C5F7F]/30 outline-none"
                          value={filters.bloodGroup}
                          onChange={(e) => setFilters(prev => ({ ...prev, bloodGroup: e.target.value }))}
                        >
                          <option value="">All Blood Groups</option>
                          {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                            <option key={bg} value={bg}>{bg}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ========================================================= */}
          {/* EXECUTIVE EXPORT DECK                                     */}
          {/* ========================================================= */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-800">
                  Select Export Format & Generate
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  High-speed export engine with formatted styling and data validation.
                </p>
              </div>
              <span className="text-xs font-medium text-slate-400">
                UTF-8 Clean Encoding
              </span>
            </div>

            {/* Format Selection Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {
                  id: 'excel',
                  label: 'Excel (.xlsx)',
                  desc: 'Formatted Workbook',
                  badge: 'Recommended',
                  icon: FileSpreadsheet,
                  color: 'emerald'
                },
                {
                  id: 'pdf',
                  label: 'PDF Document',
                  desc: dossierScope === 'individual' ? 'Executive Print View' : 'Multi-Page Binder',
                  badge: 'Print Ready',
                  icon: FileText,
                  color: 'rose'
                },
                {
                  id: 'csv',
                  label: 'CSV Data (.csv)',
                  desc: 'Raw Tabular Data',
                  badge: 'Standard',
                  icon: FileDown,
                  color: 'slate'
                },
                {
                  id: 'json',
                  label: 'JSON Archive',
                  desc: 'Structured Dataset',
                  badge: 'Raw API',
                  icon: Layers,
                  color: 'indigo'
                }
              ].map(fmt => {
                const isSelected = downloadFormat === fmt.id;
                const Icon = fmt.icon;
                return (
                  <button
                    key={fmt.id}
                    onClick={() => setDownloadFormat(fmt.id)}
                    className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer relative ${isSelected
                        ? 'border-[#1A4D6F] bg-blue-50/50 shadow-xs ring-1 ring-[#1A4D6F]'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Icon
                        className={`w-5 h-5 ${isSelected ? 'text-[#1A4D6F]' : 'text-slate-400'
                          }`}
                      />
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {fmt.badge}
                      </span>
                    </div>
                    <p className={`text-xs font-bold ${isSelected ? 'text-[#1A4D6F]' : 'text-slate-700'}`}>
                      {fmt.label}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{fmt.desc}</p>
                  </button>
                );
              })}
            </div>

            {/* Primary Download CTA */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <button
                onClick={() => handleExport(downloadFormat)}
                disabled={
                  exportLoading ||
                  (filters.reportType === 'student_dossier' && dossierScope === 'individual' && !filters.studentId) ||
                  (filters.reportType === 'student_dossier' && dossierScope === 'class' && !filters.className)
                }
                className="w-full sm:flex-1 h-12 rounded-xl bg-gradient-to-r from-[#1A4D6F] via-[#245D85] to-[#2C5F7F] text-white font-bold text-sm flex items-center justify-center gap-2 hover:opacity-95 disabled:bg-slate-300 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed shadow-md shadow-blue-900/10 transition-all cursor-pointer"
              >
                {exportLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Compiling & Generating File...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 text-blue-200" />
                    <span>
                      {filters.reportType === 'student_dossier'
                        ? (dossierScope === 'individual'
                          ? `Download ${selectedStudent?.name || 'Student'} Dossier (${downloadFormat.toUpperCase()})`
                          : dossierScope === 'class'
                            ? `Download Class ${filters.className || ''} Dossier Binder (${downloadFormat.toUpperCase()})`
                            : `Download All Students Dossier Binder (${downloadFormat.toUpperCase()})`)
                        : `Download ${currentReport.title} (${downloadFormat.toUpperCase()})`}
                    </span>
                  </>
                )}
              </button>

              {/* Direct Quick Excel Button */}
              {downloadFormat !== 'excel' && (
                <button
                  onClick={() => handleExport('excel')}
                  disabled={
                    exportLoading ||
                    (filters.reportType === 'student_dossier' && dossierScope === 'individual' && !filters.studentId) ||
                    (filters.reportType === 'student_dossier' && dossierScope === 'class' && !filters.className)
                  }
                  className="w-full sm:w-auto h-12 px-5 rounded-xl border border-emerald-600 bg-emerald-50 text-emerald-800 font-bold text-xs flex items-center justify-center gap-2 hover:bg-emerald-100 disabled:bg-slate-100 disabled:border-slate-200 disabled:text-slate-400 transition-colors cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  Quick Excel (.xlsx)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExport;
